import React, { useState } from 'react';
import { Fab, Badge, Tooltip, Zoom } from '@mui/material';
import {
  SmartToy as BotIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import AIChatModal from './AIChatModal';

/**
 * AIChatButton - Floating chat trigger button
 *
 * This component renders a floating action button that opens the AI chat modal.
 * It adapts its appearance based on the context completeness:
 * - Complete context: Highlighted, indicates full analysis available
 * - Partial context: Muted, indicates help mode
 *
 * Usage:
 *   <AIChatButton
 *     context={{
 *       page: "affordability_explorer",
 *       purpose: "Explore how life changes impact allocations",
 *       isComplete: true,
 *       currentValues: {...},
 *       computedResults: {...},
 *     }}
 *   />
 */
function AIChatButton({
  context,
  position = { bottom: 24, left: 24 },
  disabled = false,
  agentic = false,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const isComplete = context?.is_complete ?? context?.isComplete ?? false;

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Determine button appearance based on context
  const getButtonStyle = () => {
    if (disabled) {
      return {
        bgcolor: 'grey.400',
        color: 'white',
        '&:hover': { bgcolor: 'grey.500' },
      };
    }
    if (agentic) {
      return {
        bgcolor: 'primary.main',
        color: 'white',
        '&:hover': { bgcolor: 'primary.dark' },
      };
    }
    if (isComplete) {
      // Full context - highlight the button
      return {
        bgcolor: 'primary.main',
        color: 'white',
        '&:hover': { bgcolor: 'primary.dark' },
        animation: 'pulse 2s infinite',
        '@keyframes pulse': {
          '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)' },
          '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
          '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
        },
      };
    }
    // Partial context - more subtle
    return {
      bgcolor: 'grey.200',
      color: 'text.secondary',
      '&:hover': { bgcolor: 'grey.300' },
    };
  };

  const getTooltip = () => {
    if (disabled) {
      return 'AI Chat unavailable';
    }
    if (agentic) {
      return 'Chat with the Accounting Agent';
    }
    if (isComplete) {
      return 'Chat about your scenario';
    }
    return 'Get help with this form';
  };

  const getIcon = () => {
    if (agentic) {
      return <BotIcon />;
    }
    if (isComplete) {
      return <BotIcon />;
    }
    return <HelpIcon />;
  };

  return (
    <>
      <Tooltip title={getTooltip()} placement="left">
        <Zoom in>
          <Fab
            sx={{
              position: 'fixed',
              bottom: position.bottom,
              right: position.right ?? 'auto',
              left: position.left ?? 'auto',
              zIndex: 1000,
              ...getButtonStyle(),
            }}
            onClick={handleOpen}
            disabled={disabled}
            size="medium"
            aria-label={getTooltip()}
          >
            {isComplete ? (
              <Badge
                variant="dot"
                color="success"
                invisible={!isComplete}
                sx={{
                  '& .MuiBadge-badge': {
                    right: 4,
                    top: 4,
                  },
                }}
              >
                {getIcon()}
              </Badge>
            ) : (
              getIcon()
            )}
          </Fab>
        </Zoom>
      </Tooltip>

      <AIChatModal
        open={isOpen}
        onClose={handleClose}
        context={context}
        agentic={agentic}
      />
    </>
  );
}

export default AIChatButton;
